import React from 'react';
import { FormattedMessage } from 'react-intl';

import { IAccount, IImage, ITrustYouReview } from '@share/common-types';
import { IHotelDetails } from '@common-types';
import { parseVacationRentalDescription, partitionDescriptionTiles } from '@utils';

import { HotelInfo } from '../hotel-info';
import { QuintessGallery } from '../quintess-gallery';
import { QuintessInfoTiles } from '../quintess-info-tiles';

import './style.scss';

interface IProps {
  account: IAccount | null | undefined;
  hotel: IHotelDetails;
  images: IImage[];
  hotelName: string;
  hotelDescription: string[];
  reviewModule: ITrustYouReview;
  roomsRef: React.RefObject<HTMLDivElement | null>;
  overviewRef: React.RefObject<HTMLDivElement | null>;
  showMap: () => void;
  filtersAnchor: () => React.ReactNode;
  onClickScrollToReviews: (e: React.MouseEvent<HTMLDivElement>) => void;
  onClickScrollToRooms?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const OVERFLOW_TOLERANCE = 1;

/**
 * The lead paragraph, clamped. Where a supplier sends no section breaks at all this
 * is the whole description, but even a well-formed one can open with more than fits.
 * Measured while clamped, since expanding removes the clamp it is measured against.
 */
const QuintessIntro: React.FunctionComponent<{ intro: string }> = ({ intro }) => {
  const introRef = React.useRef<HTMLParagraphElement>(null);
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = React.useState<boolean>(false);

  React.useLayoutEffect(() => {
    const node = introRef.current;

    if (node) {
      setIsOverflowing(node.scrollHeight > node.clientHeight + OVERFLOW_TOLERANCE);
    }
  }, [intro]);

  return (
    <>
      <p
        ref={introRef}
        className={`quintess-images-description__intro ${isExpanded ? 'expanded' : ''}`}
      >
        {intro}
      </p>

      {!!isOverflowing && (
        <button
          type="button"
          className="quintess-images-description__intro-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <FormattedMessage id={isExpanded ? 'show.less' : 'show.more'} />
        </button>
      )}
    </>
  );
};

export const QuintessImagesDescription: React.FunctionComponent<IProps> = ({
  account,
  hotel,
  images,
  hotelName,
  hotelDescription,
  reviewModule,
  roomsRef,
  overviewRef,
  showMap,
  filtersAnchor,
  onClickScrollToReviews,
  onClickScrollToRooms,
}: IProps) => {
  // The area tiles are pulled out here and rendered under the map instead.
  const { intro, propertyTiles } = React.useMemo(() => {
    const { intro: lead, tiles } = parseVacationRentalDescription(hotelDescription);

    return { intro: lead, ...partitionDescriptionTiles(tiles) };
  }, [hotelDescription]);

  return (
    <div className="quintess-images-description">
      <QuintessGallery images={images} hotelName={hotelName} />

      <div className="hotel-details-wrapper__details-anchor">{!!hotel && filtersAnchor()}</div>

      <div ref={overviewRef} />

      <div className="hotel-details-wrapper__desktop-hotel-info">
        <HotelInfo
          hotel={hotel}
          account={account}
          showBigMap={showMap}
          reviewModule={reviewModule}
          roomsRef={roomsRef}
          onClickScrollToRooms={onClickScrollToRooms}
          onClickScrollToReviews={onClickScrollToReviews}
        />
      </div>

      <div className="quintess-images-description__content">
        {!!intro && <QuintessIntro intro={intro} />}

        <QuintessInfoTiles tiles={propertyTiles} />
      </div>
    </div>
  );
};
